import React from "react";
import { Box, Center, Spinner, Text, VStack, keyframes } from "@chakra-ui/react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Header from "@ui/layout/Header";
import Footer from "@ui/layout/Footer";
import { useAuth } from "@context/AuthContext";
import LayoutContext from "@context/LayoutContext";
import { gradients, colors, spacing } from "@theme/tokens";
import ScrollToTop from "@ui/atoms/AtomScrollToTop";

// CSS animation for page transitions - works on iOS Safari
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

function ProtectedLayout() {
  const { isAuthenticated, isSessionLoading, user } = useAuth();
  const location = useLocation();
  const [layoutVariant, setLayoutVariant] = React.useState("container");
  const [isFooterVisible, setFooterVisible] = React.useState(true);

  React.useEffect(() => {
    setLayoutVariant("container");
    setFooterVisible(true);
  }, [location.pathname]);

  const layoutContextValue = React.useMemo(
    () => ({
      variant: layoutVariant,
      setVariant: setLayoutVariant,
      isFooterVisible,
      setFooterVisible,
    }),
    [layoutVariant, isFooterVisible],
  );

  if (isSessionLoading) {
    return (
      <Center minH="100vh" bg={colors.background.darkPrimary} color={colors.text.primary}>
        <VStack spacing={4} align="center">
          <Spinner size="lg" thickness="4px" color={colors.brand.primary} />
          <Text fontSize="md" opacity={0.8}>
            Проверяем вашу сессию...
          </Text>
        </VStack>
      </Center>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  return (
    <LayoutContext.Provider value={layoutContextValue}>
      <Box position="relative" bg={colors.background.darkPrimary} w="100%" overflowX="hidden">
        <ScrollToTop />

        {/* Header */}
        <Box position="sticky" top={0} zIndex={100}>
          <Header />
        </Box>

        {/* Main content - uses CSS animation instead of framer-motion */}
        <Box
          as="main"
          position="relative"
          zIndex={1}
          key={location.pathname}
          animation={`${fadeIn} 0.25s ease-out forwards`}
          sx={{ "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
        >
          <Outlet />
        </Box>

        {/* Footer */}
        {isFooterVisible && (
          <Box position="relative" zIndex={1}>
            <Footer />
          </Box>
        )}
      </Box>
    </LayoutContext.Provider>
  );
}

export default ProtectedLayout;
